import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import Loading from 'components/common/Loading';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setLocale } from 'react-redux-i18n';
import { useParams } from 'react-router';
import Http from 'services/Http';
import { apiBase } from 'services/apiBaseUrl';
import { BACKEND_URL } from 'services/constants';
import { useSignDocumentMutation } from 'store/app/entities/OmmnioSignSlice';
import { I18n } from 'utils/i18n';
import { APP_LOCALES, writeLocaleCookie } from 'utils/localeCookie';
import { isReactNativeWebView } from 'utils/webAuthn';
import { getEmbedParentTargetOrigin } from 'utils/webOrigin';
import { normalizeOmmnioSignError } from './error-utils';
import { ommnioSignSafeAreaPadding } from './safeAreaStyles';
import './embeddedWebView.css';
import AuthenticationStep from './components/AuthenticationStep';
import ConsentDialog from './components/ConsentDialog';
import ErrorBoundary from './components/ErrorBoundary';
import PDFViewerWithOverlays from './components/PDFViewerWithOverlays';
import SuccessView from './components/SuccessView';
import useWebViewSession from './useWebViewSession';

function queuedSignMessage(type) {
  if (type === 'pdf_password_protected') {
    return I18n.t('OmmnioSign.Errors.PasswordProtected');
  }
  if (type === 'signing_unavailable') {
    return I18n.t('OmmnioSign.Errors.SigningUnavailable');
  }
  return I18n.t('OmmnioSign.Errors.SigningFailed');
}

function signErrorType(err) {
  return err?.signErrorType || err?.data?.data?.error?.type || err?.data?.error?.type || null;
}

function waitForSignTicket(ticket, streamRef) {
  return new Promise((resolve, reject) => {
    const source = new EventSource(
      `${apiBase()}/v1/ommniosign/sign/events?ticket=${encodeURIComponent(ticket)}`,
    );
    streamRef.current = source;
    let settled = false;
    let failures = 0;
    const finish = (err, payload) => {
      if (settled) {
        return;
      }
      settled = true;
      source.close();
      if (streamRef.current === source) {
        streamRef.current = null;
      }
      if (err) {
        reject(err);
      } else {
        resolve(payload);
      }
    };
    source.onmessage = event => {
      try {
        finish(null, JSON.parse(event.data));
      } catch (err) {
        finish(err);
      }
    };
    source.onerror = () => {
      failures += 1;
      if (failures >= 3) {
        finish(Object.assign(new Error('sign_failed'), { signErrorType: 'sign_failed' }));
      }
    };
  });
}

const rootSx = {
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  flex: '1 1 0',
};

const containerSx = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  minHeight: 0,
  pl: 0,
  pr: 0,
};

const loadingContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  gap: 2,
};

const fillingLayoutSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0',
  minHeight: 0,
};

const pdfWrapperSx = {
  overflowY: 'auto',
  mb: 2,
  flex: '1 1 0',
  minHeight: 0,
};

const pdfWrapperEmbeddedSx = {
  flex: '1 1 0',
  minHeight: 0,
  overflow: 'hidden',
};

const actionsSx = theme => ({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'space-between',
  alignItems: 'center',
  ...ommnioSignSafeAreaPadding,
  [theme.breakpoints.down('sm')]: {
    paddingTop: 0,
  },
});

/** When using webpack dev proxy (same-origin /api), backend still returns absolute preview URLs; rewrite to page origin. */
function resolveOmmnioPreviewUrl(previewUrl) {
  if (!previewUrl || typeof previewUrl !== 'string') return previewUrl;
  if (typeof window === 'undefined') return previewUrl;
  if (BACKEND_URL) return previewUrl;
  try {
    const u = new URL(previewUrl);
    if (u.pathname.includes('/ommniosign/') && u.pathname.includes('/preview')) {
      return `${window.location.origin}${u.pathname}${u.search}`;
    }
  } catch {
    /* ignore */
  }
  return previewUrl;
}

const STEPS = {
  LOADING: 'loading',
  FILLING: 'filling',
  AUTHENTICATION: 'authentication',
  CONSENT: 'consent',
  SIGNING: 'signing',
  SUCCESS: 'success',
  ERROR: 'error',
};

const OmmnioSignScene = ({
  requestId: requestIdProp,
  signerToken: signerTokenProp,
  onComplete,
  onCancel,
  onDecline,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  // Use props if provided (for embedded use), otherwise use URL params (for route use)
  const requestId = requestIdProp || params.requestId;
  const signerToken = signerTokenProp || params.signerToken;
  const [signDocument] = useSignDocumentMutation();
  const webViewSessionStatus = useWebViewSession();

  // State
  const [declining, setDeclining] = useState(false);
  const [step, setStep] = useState(STEPS.LOADING);
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [, setSigner] = useState(null);
  const [, setRequester] = useState(null);
  const [settings, setSettings] = useState(null);
  const [fields, setFields] = useState([]);
  const [pdfData, setPdfData] = useState(null);
  const [widgets, setWidgets] = useState({});
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const [, setAuthMethod] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [evidenceId, setEvidenceId] = useState(null);
  const [, setConsent] = useState(null);
  const [signatureDetails, setSignatureDetails] = useState(null);
  const [error, setError] = useState(null);
  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false);
  const [viewStartTime] = useState(Date.now());
  const [currentSignerIndex, setCurrentSignerIndex] = useState(null);
  const [isEmbeddedWebView] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return (
      isReactNativeWebView() || document.documentElement.classList.contains('ommniosign-embedded')
    );
  });

  // Mark embedded WebView and sync page background to MUI paper (avoids black WebView gap).
  useEffect(() => {
    if (!isEmbeddedWebView) {
      return undefined;
    }
    document.documentElement.classList.add('ommniosign-embedded');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.style.setProperty(
      '--ommnio-embedded-bg',
      prefersDark ? '#1a1a24' : '#ffffff',
    );
    return () => {
      document.documentElement.classList.remove('ommniosign-embedded');
      document.documentElement.style.removeProperty('--ommnio-embedded-bg');
    };
  }, [isEmbeddedWebView]);

  // eIDAS compliance tracking
  const [documentViewed, setDocumentViewed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  // Normalize field properties (backend uses 'required', frontend uses 'mandatory')
  const normalizeFields = inputFields => {
    if (!inputFields || !Array.isArray(inputFields)) return inputFields;
    return inputFields.map(field => ({
      ...field,
      // Ensure both mandatory and required properties exist
      mandatory: field.mandatory ?? field.required ?? false,
      required: field.required ?? field.mandatory ?? false,
    }));
  };

  // Map backend error codes to user-friendly messages
  const mapBackendError = err => {
    const errorCode = err.response?.data?.reason;

    switch (errorCode) {
      case 'expired':
        return I18n.t('OmmnioSign.Errors.RequestExpired');
      case 'already_signed':
        return I18n.t('OmmnioSign.Errors.AlreadySigned');
      case 'not_your_turn':
        return I18n.t('OmmnioSign.Errors.NotYourTurn');
      default:
        return err.message || I18n.t('OmmnioSign.Errors.InvalidSigningLink');
    }
  };

  // Load signature request and PDF
  useEffect(() => {
    if (webViewSessionStatus === 'pending') {
      return;
    }
    const loadDocument = async () => {
      try {
        setStep(STEPS.LOADING);

        // Load signing page data from new API
        const response = await Http.get(`/v1/ommniosign/sign/${requestId}/${signerToken}`);

        if (!response.status) {
          throw new Error(response.data?.error || 'Failed to load document');
        }

        const responseData = response.data;

        // Prefer employee settings language from the API over cookie/navigator.
        const locale = responseData.locale;
        if (typeof locale === 'string' && APP_LOCALES.includes(locale)) {
          moment().locale(locale);
          moment.locale(locale);
          dispatch(setLocale(locale));
          writeLocaleCookie(locale);
        }

        // Set state from API response
        setSignatureRequest(responseData);
        setSigner(responseData.signer);
        setRequester(responseData.requester);
        setSettings(responseData.settings);

        // Backend already filtered fields for current signer
        const normalizedFields = normalizeFields(responseData.fields);
        setFields(normalizedFields);

        // For UI purposes, find signer index if needed
        if (responseData.signers && responseData.signers.length > 0) {
          const signerIndex =
            responseData.signers.findIndex(s => s.signerToken === signerToken) ?? null;
          setCurrentSignerIndex(signerIndex);
        } else {
          setCurrentSignerIndex(null);
        }

        // Download PDF preview (same-origin when dev proxy + ngrok)
        const previewUrl = resolveOmmnioPreviewUrl(responseData.document.previewUrl);
        const pdfResponse = await fetch(previewUrl, {
          credentials: 'include', // Still needed for Ommnio delivery
        });

        if (!pdfResponse.ok) {
          throw new Error(
            `Failed to download PDF: ${pdfResponse.status} ${pdfResponse.statusText}`,
          );
        }

        const blob = await pdfResponse.blob();
        setPdfData(blob);
        setDocumentViewed(true); // Mark document as viewed for eIDAS compliance

        setStep(STEPS.FILLING);
      } catch (err) {
        console.error('Error loading document:', err);
        const errorMessage = mapBackendError(err);
        setError(errorMessage);
        setStep(STEPS.ERROR);
      }
    };

    loadDocument();
  }, [dispatch, requestId, signerToken, webViewSessionStatus]);

  // Handle widget value changes
  const handleWidgetChange = useCallback((fieldId, value) => {
    setWidgets(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Handle all mandatory fields being filled (or unfilled)
  const handleAllFieldsFilled = useCallback(isFilled => {
    setAllFieldsFilled(isFilled);
  }, []);

  // Invalidate session-derived auth whenever any widget value changes. A signer who
  // edited fields after a reused-session authorization must re-authorize (silently, if
  // the session is still active) so the signed payload always matches what was authorized.
  useEffect(() => {
    setEvidenceId(null);
    setAuthData(null);
  }, [widgets]);

  // Handle continuing to authentication
  const handleContinueToAuth = useCallback(() => {
    // Backend already filtered fields for current signer, so just check all fields
    if (fields) {
      const missingFields = fields.filter(field => {
        const fieldKey = field?._id || field?.id || field?.name;
        return (field.required || field.mandatory) && !widgets[fieldKey];
      });

      if (missingFields.length > 0) {
        setError(I18n.t('OmmnioSign.Errors.RequiredFieldsMissing'));
        return;
      }
    }

    setError(null);
    if (settings?.requireAuthentication === 'none') {
      setStep(STEPS.CONSENT);
      return;
    }

    if (evidenceId) {
      setStep(STEPS.CONSENT);
      return;
    }

    setStep(STEPS.AUTHENTICATION);
  }, [fields, widgets, settings?.requireAuthentication, evidenceId]);

  // Handle authentication completion
  const handleAuthComplete = useCallback((method, data, evidence) => {
    setAuthMethod(method);
    setAuthData(data);
    if (evidence) {
      setEvidenceId(evidence); // Store evidenceId from OTP verification
    }
    setStep(STEPS.CONSENT);
  }, []);

  // Perform the actual signing
  const signStreamRef = useRef(null);
  useEffect(() => () => signStreamRef.current?.close(), []);

  const performSigning = useCallback(
    async consentData => {
      try {
        setStep(STEPS.SIGNING);

        const viewDurationSeconds = Math.floor((Date.now() - viewStartTime) / 1000);

        const result = await signDocument({
          signerToken,
          evidenceId,
          widgets,
          consent: {
            ...consentData,
            // eIDAS compliance fields
            documentViewed,
            viewDurationSeconds,
            scrolledToBottom,
            acceptanceMethod: 'button_click', // User clicked the "Accept and Sign" button
          },
        }).unwrap();

        const payload = result.data?.ticket
          ? await waitForSignTicket(result.data.ticket, signStreamRef)
          : result;

        if (payload.status && payload.data?.evidenceId) {
          setEvidenceId(payload.data.evidenceId);
          setSignatureDetails(payload.data.signature);
          setStep(STEPS.SUCCESS);
        } else {
          throw Object.assign(new Error(payload.error?.type || 'sign_failed'), {
            signErrorType: payload.error?.type || 'sign_failed',
          });
        }
      } catch (err) {
        console.error('Error signing document:', err);
        const type = signErrorType(err);
        setError(
          type
            ? queuedSignMessage(type)
            : normalizeOmmnioSignError(err, I18n.t('OmmnioSign.Errors.SigningFailed')),
        );
        setStep(STEPS.ERROR);
      }
    },
    [
      signerToken,
      evidenceId,
      widgets,
      signDocument,
      viewStartTime,
      documentViewed,
      scrolledToBottom,
    ],
  );

  // Handle consent acceptance
  const handleConsentAccept = useCallback(
    consentData => {
      setConsent(consentData);
      performSigning(consentData);
    },
    [performSigning],
  );

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (step === STEPS.AUTHENTICATION) {
      setStep(STEPS.FILLING);
    } else if (step === STEPS.CONSENT) {
      if (authData?.sessionActive) {
        setStep(STEPS.FILLING);
      } else {
        setStep(STEPS.AUTHENTICATION);
      }
    }
  }, [step, authData?.sessionActive]);

  const handleCancel = useCallback(() => {
    const message = {
      type: 'OMMNIOSIGN_CANCELLED',
      requestId: signatureRequest?.requestId,
      externalId: signatureRequest?.externalId,
      externalType: signatureRequest?.externalType,
    };

    if (onCancel) {
      onCancel(message);
    } else if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else if (window.parent !== window) {
      window.parent.postMessage(message, getEmbedParentTargetOrigin());
    }
  }, [signatureRequest, onCancel]);

  const closeDeclined = useCallback(
    message => {
      if (onDecline) {
        onDecline(message);
      } else if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      } else if (window.parent !== window) {
        window.parent.postMessage(message, getEmbedParentTargetOrigin());
      } else if (onCancel) {
        onCancel(message);
      }
    },
    [onCancel, onDecline],
  );

  const handleDecline = useCallback(async () => {
    if (
      signatureRequest?.externalType !== 'message' ||
      !signatureRequest?.externalId ||
      declining
    ) {
      return;
    }
    const message = {
      type: 'OMMNIOSIGN_DECLINED',
      requestId: signatureRequest.requestId,
      externalId: signatureRequest.externalId,
      externalType: signatureRequest.externalType,
    };
    setDeclining(true);
    if (window.ReactNativeWebView?.postMessage && !onDecline) {
      closeDeclined(message);
      return;
    }
    try {
      await Http.post(`/v1/signatures/${signatureRequest.externalId}/decline`);
      closeDeclined(message);
    } catch (declineError) {
      setDeclining(false);
      setError(declineError?.data?.error?.message || declineError?.message || '');
    }
  }, [closeDeclined, declining, onDecline, signatureRequest]);

  // Render loading state
  if (step === STEPS.LOADING) {
    return (
      <Box sx={rootSx}>
        <Container sx={containerSx}>
          <Box sx={loadingContainerSx}>
            <div>
              <Loading size={60} />
              <Typography variant="h6">{I18n.t('OmmnioSign.LoadingDocument')}</Typography>
            </div>
          </Box>
        </Container>
      </Box>
    );
  }

  // Render error state
  if (step === STEPS.ERROR) {
    return (
      <Box sx={rootSx}>
        <Container sx={containerSx}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  // Render success state
  if (step === STEPS.SUCCESS) {
    return (
      <Box sx={rootSx}>
        <Container sx={containerSx}>
          <SuccessView
            signatureRequest={signatureRequest}
            signatureDetails={signatureDetails}
            evidenceId={evidenceId}
            onComplete={onComplete}
          />
        </Container>
      </Box>
    );
  }

  // Render signing state
  if (step === STEPS.SIGNING) {
    return (
      <Box sx={rootSx}>
        <Container sx={containerSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={60} />
            <Typography variant="h6">{I18n.t('OmmnioSign.Signing')}</Typography>
            <Typography variant="body2" color="textSecondary">
              {I18n.t('OmmnioSign.SigningDescription')}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={[rootSx, isEmbeddedWebView && { height: '100%' }]}>
      <Container sx={[containerSx, isEmbeddedWebView && { height: '100%', flexGrow: 1 }]}>
        {/* Header */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* PDF Viewer with overlay fields (SignRequest-style) */}
        {step === STEPS.FILLING && pdfData && (
          <Box
            sx={[fillingLayoutSx, isEmbeddedWebView && { height: '100%' }]}
            className={isEmbeddedWebView ? 'ommniosign-filling-layout' : undefined}
          >
            <Box
              sx={isEmbeddedWebView ? pdfWrapperEmbeddedSx : pdfWrapperSx}
              className={isEmbeddedWebView ? 'ommniosign-pdf-wrapper' : undefined}
            >
              <PDFViewerWithOverlays
                pdfData={pdfData}
                fields={fields}
                widgets={widgets}
                onWidgetChange={handleWidgetChange}
                onAllFieldsFilled={handleAllFieldsFilled}
                onScrollToBottom={() => setScrolledToBottom(true)}
                currentSignerIndex={currentSignerIndex}
                compact={isEmbeddedWebView}
              />
            </Box>
            <Box
              sx={actionsSx}
              className={isEmbeddedWebView ? 'ommniosign-embedded-actions' : undefined}
            >
              {signatureRequest?.externalType === 'message' && signatureRequest?.externalId ? (
                <Button
                  color="secondary"
                  onClick={() => setDeclineConfirmOpen(true)}
                  disabled={declining}
                >
                  {I18n.t('AcknowledgementMessage.Decline')}
                </Button>
              ) : null}
              {isEmbeddedWebView ? null : (
                <Button onClick={handleCancel}>{I18n.t('Common.Cancel')}</Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinueToAuth}
                disabled={!allFieldsFilled}
                sx={{ ml: 'auto' }}
              >
                {I18n.t('Common.Continue')}
              </Button>
            </Box>
            <Dialog
              open={declineConfirmOpen}
              onClose={() => setDeclineConfirmOpen(false)}
              aria-labelledby="decline-signature-title"
            >
              <DialogTitle id="decline-signature-title">
                {I18n.t('AcknowledgementMessage.Decline')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {I18n.t('AcknowledgementMessage.Decline confirmation')}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeclineConfirmOpen(false)}>
                  {I18n.t('Common.Cancel')}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => {
                    setDeclineConfirmOpen(false);
                    handleDecline();
                  }}
                  disabled={declining}
                >
                  {I18n.t('AcknowledgementMessage.Decline')}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Authentication */}
        {step === STEPS.AUTHENTICATION && (
          <AuthenticationStep
            signerToken={signerToken}
            widgets={widgets}
            documentName={signatureRequest?.document?.name}
            deliveryChannel={signatureRequest?.signer?.deliveryChannel}
            signerPhone={signatureRequest?.signer?.phone}
            companyId={signatureRequest?.companyId}
            onAuthComplete={handleAuthComplete}
            onBack={handleBack}
          />
        )}

        {/* Consent */}
        {step === STEPS.CONSENT && (
          <ConsentDialog
            open={step === STEPS.CONSENT}
            documentName={signatureRequest?.document?.name}
            onAccept={handleConsentAccept}
            onBack={handleBack}
          />
        )}
      </Container>
    </Box>
  );
};

const OmmnioSignSceneWithErrorBoundary = props => (
  <ErrorBoundary>
    <OmmnioSignScene {...props} />
  </ErrorBoundary>
);

export default OmmnioSignSceneWithErrorBoundary;
